import { icsUrl, googleCalendarUrl, linkedInShareUrl, twitterShareUrl } from '../lib/api.js'
import { BTN_COMPACT_PRIMARY, BTN_COMPACT_BRAND, BTN_COMPACT_GHOST, BTN_COMPACT_ICON } from '../lib/ui.js'
import SubstackShareButton from './SubstackShareButton.jsx'

// #350: ONE calendar/social bar, rendered under every event.
//
// It used to live inline in EventCard and nowhere else, which meant a talk that
// happened to belong to a conference — still an event, still something you
// would want in your calendar — had no way to be added or shared from the feed
// at all. Extracted here so the standalone card and every nested talk row get
// the identical bar rather than one of them getting nothing.
//
// `relative z-10` is not decoration: both cards lay a stretched link over their
// whole surface, and without it that link swallows every click in this bar.
//
// The pills are deliberately smaller than the 44px targets used for page-level
// actions (EventActions on the detail page keeps those). A card can carry
// several of these bars, so at 44px a three-talk conference turned into a wall
// of buttons. #354 moved the spelling into lib/ui.js so it is one definition
// rather than a local copy.

export default function EventActionBar({ event, className = 'mt-2' }) {
  if (!event?.id) return null
  return (
    <div className={`relative z-10 flex flex-wrap items-center gap-1 ${className}`}>
      <a
        href={googleCalendarUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        className={BTN_COMPACT_PRIMARY}
      >
        Add to Google Calendar
      </a>
      <a
        href={icsUrl(event.id)}
        download
        className={BTN_COMPACT_BRAND}
      >
        Download .ics
      </a>
      {event.registrationUrl ? (
        <a
          href={event.registrationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={BTN_COMPACT_GHOST}
        >
          Register ↗
        </a>
      ) : null}
      <a
        href={linkedInShareUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
        className={`${BTN_COMPACT_ICON} gap-1`}
      >
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
        <span className="hidden sm:inline">Share</span>
      </a>
      <a
        href={twitterShareUrl(event)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X (Twitter)"
        title="Share on X (Twitter)"
        className={BTN_COMPACT_ICON}
      >
        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </a>
      <SubstackShareButton event={event} className={BTN_COMPACT_ICON} iconSize="h-3.5 w-3.5" />
    </div>
  )
}
